"use client";

import { useState } from "react";
import Image from "next/image";
import { ImageIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ImageProps } from "next/image";

interface SafeImageProps extends Omit<ImageProps, "src"> {
  src?: ImageProps["src"];
  fallbackClassName?: string;
}

export function SafeImage({
  fallbackClassName,
  className,
  onError,
  src,
  alt,
  ...props
}: SafeImageProps) {
  const [hasError, setHasError] = useState(false);

  const showFallback = !src || (typeof src === "string" && src.trim() === "") || hasError;

  if (showFallback) {
    if (props.fill) {
      return (
        <div
          className={cn(
            "absolute inset-0 flex items-center justify-center bg-gray-50/80 text-gray-300",
            fallbackClassName
          )}
        >
          <ImageIcon className="h-10 w-10" strokeWidth={1} />
        </div>
      );
    }
    return (
      <div
        className={cn(
          "flex items-center justify-center bg-gray-50/80 text-gray-300",
          fallbackClassName
        )}
        style={{ width: props.width, height: props.height }}
      >
        <ImageIcon className="h-10 w-10" strokeWidth={1} />
      </div>
    );
  }

  return (
    <Image
      src={src}
      alt={alt}
      className={className}
      onError={(e) => {
        setHasError(true);
        onError?.(e);
      }}
      {...props}
    />
  );
}
